/**
 * Agent Prompt Pattern Library — canonical registry.
 *
 * The registry is version-controlled code; selected presets live in the
 * canonical Settings store. This module validates registry integrity and
 * cross-references against the multi-agent role registry.
 *
 * No network call. No DB. No provider. No execution.
 */
import { getMultiAgentRoleById } from "../multiAgent/roles.js";
import { AGENT_PROMPT_PRESETS, getAgentPromptPreset } from "./presets.js";
import { AGENT_PROMPT_RUNTIME_SURFACES, getAgentPromptSurfaceByRoleId, } from "./surfaces.js";
export { getAgentPromptPreset };
export function listAgentPromptPatterns() {
    return AGENT_PROMPT_PRESETS;
}
export function listAgentPromptPatternIds() {
    return AGENT_PROMPT_PRESETS.map((p) => p.id);
}
export function isKnownAgentPromptPreset(id) {
    return getAgentPromptPreset(id) !== undefined;
}
export function validateAgentPromptPatternRegistry() {
    const errors = [];
    // The surface catalog itself must stay coherent: unique roles/surfaces and
    // every cataloged role resolvable in the canonical role registry.
    const surfaceRoles = new Set();
    const surfaceIds = new Set();
    for (const surface of AGENT_PROMPT_RUNTIME_SURFACES) {
        if (surfaceRoles.has(surface.roleId)) {
            errors.push(`Runtime surface catalog has duplicate role: ${surface.roleId}`);
        }
        surfaceRoles.add(surface.roleId);
        if (surfaceIds.has(surface.surfaceId)) {
            errors.push(`Runtime surface catalog has duplicate surfaceId: ${surface.surfaceId}`);
        }
        surfaceIds.add(surface.surfaceId);
        if (!getMultiAgentRoleById(surface.roleId)) {
            errors.push(`Runtime surface catalog references unknown role: ${surface.roleId}`);
        }
    }
    const ids = new Set();
    for (const preset of AGENT_PROMPT_PRESETS) {
        if (ids.has(preset.id))
            errors.push(`Duplicate preset id: ${preset.id}`);
        ids.add(preset.id);
        if (!preset.id)
            errors.push("Preset missing id");
        if (!preset.version)
            errors.push(`Preset ${preset.id} missing version`);
        if (!preset.title)
            errors.push(`Preset ${preset.id} missing title`);
        if (preset.roleIntent.length === 0)
            errors.push(`Preset ${preset.id} missing roleIntent`);
        if (preset.operatingPattern.length === 0)
            errors.push(`Preset ${preset.id} missing operatingPattern`);
        if (preset.compatibleRoles.length === 0) {
            errors.push(`Preset ${preset.id} missing compatibleRoles`);
        }
        for (const roleId of preset.compatibleRoles) {
            if (!getMultiAgentRoleById(roleId)) {
                errors.push(`Preset ${preset.id} references unknown role: ${roleId}`);
            }
            // Every compatible role must resolve to a cataloged runtime surface so
            // support is explicit and auditable; a missing entry means the catalog
            // was not extended when the role/preset was added.
            if (getAgentPromptSurfaceByRoleId(roleId) === undefined) {
                errors.push(`Preset ${preset.id} references role with no runtime surface entry: ${roleId}`);
            }
        }
        if (preset.compatibleExecutionModes.length === 0) {
            errors.push(`Preset ${preset.id} missing compatibleExecutionModes`);
        }
        if (!preset.compatibleExecutionModes.includes(preset.defaultExecutionMode)) {
            errors.push(`Preset ${preset.id} defaultExecutionMode ${preset.defaultExecutionMode} not in compatibleExecutionModes`);
        }
        if (preset.safety.grantsAuthority !== false)
            errors.push(`Preset ${preset.id} grantsAuthority must be false`);
        if (preset.safety.mayOverrideToolPolicy !== false)
            errors.push(`Preset ${preset.id} mayOverrideToolPolicy must be false`);
        if (preset.safety.mayBypassApproval !== false)
            errors.push(`Preset ${preset.id} mayBypassApproval must be false`);
        if (preset.safety.mayBypassEmergencyStop !== false) {
            errors.push(`Preset ${preset.id} mayBypassEmergencyStop must be false`);
        }
        for (const prov of preset.sourceProvenance) {
            if (!prov.patternSummary)
                errors.push(`Preset ${preset.id} provenance missing patternSummary`);
            if (prov.sourceType === "external-reference" && (!prov.repository || !prov.sourceCommit)) {
                errors.push(`Preset ${preset.id} external provenance must pin repository and sourceCommit`);
            }
        }
    }
    return { valid: errors.length === 0, errors };
}
//# sourceMappingURL=registry.js.map